import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  clearAuditTrail,
  fetchApplicationAuditTrail,
} from "../../../../store/reducers/applicationSlice";
import "./AuditTrailModal.css";

const HIDDEN_ACTIONS = new Set(["VIEWED", "ACCESS_DENIED", "PDF_GENERATED"]);

const ACTION_LABELS = {
  CREATED: "Created",
  UPDATED: "Updated",
  STATE_CHANGED: "Status Changed",
  DISCREPANCY_UPDATED: "Discrepancy Updated",
  FILES_UPLOADED: "Files Uploaded",
  FILES_REMOVED: "Files Removed",
  CERTIFICATE_ISSUED: "Certificate Issued",
  CERTIFICATE_UPDATED: "Certificate Updated",
  CERTIFICATE_DOWNLOADED: "Certificate Downloaded",
  FILES_DOWNLOADED: "Files Downloaded",
  RENEWAL_REQUESTED: "Renewal Requested",
  RENEWAL_REMINDER_SENT: "Renewal Reminder",
  DELETED: "Deleted",
};

const AuditTrailModal = ({ open, onClose, applicationId, accessToken }) => {
  const dispatch = useDispatch();
  const { logs, loading, error, pagination } = useSelector(
    (state) => state.applications.auditTrail
  );
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    if (!open || !applicationId || !accessToken) return;
    dispatch(
      fetchApplicationAuditTrail({
        accessToken,
        id: applicationId,
        page,
        limit: 10,
        action: actionFilter || undefined,
      })
    );
  }, [open, applicationId, accessToken, page, actionFilter, dispatch]);

  useEffect(() => {
    if (!open) {
      setPage(1);
      setActionFilter("");
      dispatch(clearAuditTrail());
    }
  }, [open, dispatch]);

  if (!open) return null;

  const visibleLogs = (logs || []).filter(
    (log) => !HIDDEN_ACTIONS.has(log.action)
  );

  return (
    <div className="audit-modal-overlay" onClick={onClose}>
      <div
        className="audit-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="audit-modal-header">
          <h2>Application Audit Trail</h2>
          <button type="button" className="audit-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="audit-modal-filters">
          <label>
            Filter by action
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All actions</option>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="audit-modal-body">
          {loading ? (
            <div className="audit-modal-loading">Loading audit trail...</div>
          ) : error ? (
            <div className="audit-modal-error">{error}</div>
          ) : visibleLogs.length === 0 ? (
            <div className="audit-modal-empty">No audit entries found.</div>
          ) : (
            <ul className="audit-timeline">
              {visibleLogs.map((log) => (
                <li key={log.id} className="audit-timeline-item">
                  <div className="audit-timeline-badge">
                    {ACTION_LABELS[log.action] || log.action}
                  </div>
                  <div className="audit-timeline-main">
                    <p className="audit-timeline-desc">
                      {log.description || "—"}
                    </p>
                    <div className="audit-timeline-meta">
                      <span>
                        {log.performed_by_name ||
                          (log.actor_type === "SYSTEM" ? "System" : "Unknown")}
                        {log.performed_by_role
                          ? ` (${log.performed_by_role})`
                          : ""}
                      </span>
                      <span>
                        {log.created_at
                          ? moment(log.created_at).format("YYYY-MM-DD HH:mm")
                          : "—"}
                      </span>
                    </div>
                    {log.remarks ? (
                      <p className="audit-timeline-remarks">
                        Remarks: {log.remarks}
                      </p>
                    ) : null}
                    {log.from_state_name || log.to_state_name ? (
                      <p className="audit-timeline-states">
                        {log.from_state_name || "—"} →{" "}
                        {log.to_state_name || "—"}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="audit-modal-footer">
          <button
            type="button"
            className="audit-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {pagination.currentPage || page} of{" "}
            {pagination.totalPages || 1}
          </span>
          <button
            type="button"
            className="audit-page-btn"
            disabled={
              loading ||
              (pagination.totalPages || 1) <= (pagination.currentPage || page)
            }
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailModal;
